import { FlashFilled } from '@carbon/icons-react';
import { Sparkle } from '@routable/ai';
import { Button, Dialog, Tooltip, useModalState } from '@routable/components';
import { featureFlag, useFeatureFlag } from '@routable/feature-configs';
import { createItemFormFields } from '@routable/shared';
import clsx from 'clsx';
import debounce from 'es6-promise-debounce';
import React, { useState } from 'react';
import { useEffectOnce } from 'react-use';
import { Field } from 'redux-form';
import { ReduxFormGrossDsComboBox } from 'gross-ds/ReduxFormGrossDsComboBox';
import { ArrowRenderer, FormControl } from 'components/form';
import { AsyncSelect, selectComponents } from 'components/selectV2';
import { isMobileOS } from 'helpers/browser';
import { getFieldErrors } from 'helpers/errors';
import { createPartnershipSelectName, getVendorOrCustomerTitleFromItemKind } from 'helpers/partnerships';
import { isSelectedPartnerTypeNew } from 'helpers/searchCompanies';
import { returnFullOptionObjectAsValueForSelects } from 'helpers/selects';
import { capitalize } from 'helpers/stringHelpers';
import { useOverlaysSourceForFieldValue } from 'hooks';
import { useCreateItemFormContext } from 'modules/dashboard/createItems/createItemForm/components/CreateItemForm/context';
import { isSelectedCompanyExisting } from 'modules/dashboard/createItems/createItemForm/helpers/partner';
import { MultiLineOptionWithTooltipForSearchCompanies } from 'modules/selects/components';
import { checkPartnershipEligibilityForSearchCompanies } from 'modules/selects/helpers/logic';
import { componentHelpers, textHelpers } from './helpers';
import './SearchCompanies.scss';
const SearchCompanies = (props) => {
    const { defaultOptions = [], errors = {}, formName, formUI, isFetchingPartnership, isLoading, item, label, onLoad, partnershipType, setHasManuallyChosenPartnership, } = props;
    const shouldUseNewPartnershipSelect = useFeatureFlag(featureFlag.Enum.use_new_partnership_select);
    const vendorsPayablesOnlyEnabled = useFeatureFlag(featureFlag.Enum.global_only_vendors_payables_only);
    const [options, setOptions] = useState(defaultOptions);
    const confirmModalState = useModalState({});
    const [companyToBeUpdated, setCompanyToBeUpdated] = useState(null);
    const [isFetching, setIsFetching] = useState(false);
    const { isFetchingLedgerOnlyPartnership, setIsFetchingLedgerOnlyPartnership } = useCreateItemFormContext();
    const { isItemEdit, selectedCompany } = Object(formUI);
    const isCompanyNew = isSelectedPartnerTypeNew(selectedCompany?.type);
    const isCompanyExisting = isSelectedCompanyExisting(selectedCompany);
    const kindTitle = partnershipType || getVendorOrCustomerTitleFromItemKind(item?.kind);
    const searchPartnerships = async (input) => {
        setIsFetching(true);
        setOptions([]);
        const suffix = item?.kind || partnershipType;
        const resultOptions = await componentHelpers.getPartnershipSearchOptions(input, formName, suffix);
        const { companyIntegrationSettings, companySettings, ledger, ledgerCurrencies, location, supportedCurrencies } = props;
        const newOptions = resultOptions.map((option) => {
            const { isDisabled } = checkPartnershipEligibilityForSearchCompanies({
                companyIntegrationSettings,
                companySettings,
                formPartnershipType: partnershipType,
                ledger,
                ledgerCurrencies,
                location,
                partnership: option,
                supportedCurrencies,
                vendorsPayablesOnlyEnabled,
            });
            return {
                ...option,
                description: `Legal name: ${option.partner?.name}`,
                label: option.name,
                isDisabled,
                value: option.id,
            };
        });
        setOptions(newOptions);
        setIsFetching(false);
        return newOptions;
    };
    const debounceSearch = debounce(searchPartnerships, 400);
    const handleSelectionChange = (value) => {
        componentHelpers.handleSelectCompanyChange(value, props, {
            setHasManuallyChosenPartnership,
            setIsFetchingLedgerOnlyPartnership,
        });
    };
    const onHandleSelectionChange = (value) => {
        if (isCompanyExisting) {
            setCompanyToBeUpdated(value);
            confirmModalState.open();
            return;
        }
        handleSelectionChange(value);
    };
    useEffectOnce(() => {
        if (onLoad) {
            onLoad();
        }
        const { location } = props;
        if (typeof location.state === 'object' && !!location.state && 'selectedCompany' in location.state) {
            handleSelectionChange(location.state.selectedCompany);
        }
    });
    const getFieldLabel = () => {
        if (label) {
            return label;
        }
        if (isCompanyNew || isCompanyExisting) {
            return 'Routable display name';
        }
        return capitalize(item?.kind ? getVendorOrCustomerTitleFromItemKind(item.kind) : partnershipType);
    };
    const fieldLabel = getFieldLabel();
    const companyNameErrors = getFieldErrors(errors, 'companyName');
    const selectAsyncClasses = clsx('search-companies--select', { error: !!companyNameErrors });
    const isLoadingField = isFetchingPartnership || isFetchingLedgerOnlyPartnership || isLoading;
    const { currentSource, isPredictiveCodingUiEnabled } = useOverlaysSourceForFieldValue(createItemFormFields.SELECTED_COMPANY);
    return (React.createElement(FormControl, { className: "!mb-0" },
        React.createElement("div", { className: "w-full" }, shouldUseNewPartnershipSelect ? (React.createElement(Field, { autoFocus: !selectedCompany, blurInputOnSelect: !isMobileOS(), component: ReduxFormGrossDsComboBox, components: {
                Option: MultiLineOptionWithTooltipForSearchCompanies,
            }, createLabel: `Create a new ${kindTitle}: `, dataFullStory: true, disabled: isItemEdit, errors: errors, fieldVal: selectedCompany?.label || selectedCompany?.name, getNewOptData: (inputVal) => ({
                __isNew__: true,
                companyName: inputVal,
                id: inputVal,
                label: inputVal,
                value: inputVal,
            }), isCreatable: true, isFetching: isFetching, isLoading: isLoadingField, label: fieldLabel, labelRight: currentSource && (React.createElement(Tooltip, { "data-testid": "data-source-tooltip-purchase-order", tooltip: isPredictiveCodingUiEnabled && currentSource, variant: "dark" }, isPredictiveCodingUiEnabled ? (React.createElement(Sparkle, { size: 16 })) : (React.createElement(FlashFilled, { className: "text-magenta-50", size: 16, title: currentSource })))), loadingText: `Looking up ${kindTitle}s`, name: "selectedCompany", onChange: onHandleSelectionChange, onClear: () => onHandleSelectionChange(null), onInputChange: debounceSearch, options: options, placeholder: `Search for a ${kindTitle} or create a new one`, required: true, size: "large" })) : (React.createElement(AsyncSelect, { autoFocus: !selectedCompany, blurInputOnSelect: !isMobileOS(), className: selectAsyncClasses, clearIndicatorAlertMessage: textHelpers.getClearCompanyConfirmationText(kindTitle), clearIndicatorShouldConfirm: isSelectedPartnerTypeNew(selectedCompany?.type), components: {
                DropdownIndicator: ArrowRenderer,
                ClearIndicator: selectComponents.ClearIndicatorWithConfirm,
                Option: MultiLineOptionWithTooltipForSearchCompanies,
            }, dataFullStory: true, defaultOptions: defaultOptions, errors: errors, filterConfig: { ignoreCase: true }, getCreateLabel: (input) => `Create a new ${kindTitle} "${input}"`, getIsValidNewOption: (input) => input?.length > 0, getNewOptData: (inputVal, optLabel) => ({
                __isNew__: true,
                companyName: inputVal,
                id: inputVal,
                label: optLabel,
            }), getOptionLabel: (opt) => createPartnershipSelectName(opt) || opt.label, getOptionValue: (opt) => opt.id, idPrefix: "selectedCompany-", input: {
                name: 'selectedCompany',
                onChange: handleSelectionChange,
                value: selectedCompany || null,
            }, isCreatable: true, isDisabled: isItemEdit, isLoading: isLoadingField, isMulti: false, label: fieldLabel, loadOptions: debounceSearch, parseValue: returnFullOptionObjectAsValueForSelects, placeholder: `Search for a ${kindTitle} or create a new one*`, valueKey: "id" }))),
        React.createElement(Dialog, { footer: React.createElement(Dialog.Footer, null,
                React.createElement(Button, { "data-testid": "cancel-confirm-modal-button", onPress: confirmModalState.close, variant: "outline" }, "Cancel"),
                React.createElement(Button, { "data-testid": "close-confirm-modal-button", onPress: () => {
                        handleSelectionChange(companyToBeUpdated);
                        confirmModalState.close();
                    } }, "Confirm")), header: React.createElement(Dialog.Header, { label: "Are you sure?" }), state: confirmModalState },
            React.createElement("span", { className: "text-sm text-grey-70" }, "You will lose all progress if you continue."))));
};
export default SearchCompanies;
